import {
  APPOINTMENT_CHANNEL,
  APPOINTMENT_STATUS,
  MODES_OF_APT,
  SupportedTeleProviders,
  TagRecord,
  TYPES_OF_APT,
} from "@elixir/types";
import { logAndGetErrorResponse } from "../error";
import { ControllerReturnType } from "../types";
import {
  createAppointment,
  getMe,
  sendAppointmentCommunication,
} from "../../utils/bridges/methods";
import generateAppointmentId from "./generate-appointment-id";
import {
  convertDateToFirestoreTimestamp,
  isoStringToTimestamp,
} from "../../firestore/utils";
import dayjs from "dayjs";
import getB from "../../utils/bridges/EkaBridge";
import { generateAppointmentToken } from "./generate-appointment-token";
import { LocalPatient } from "../../utils/bridges/types/patient";
import { MESSAGE_TYPE } from "../../utils/bridges/events/types";
import {
  trackAppointmentActivity,
  AptActivityType,
} from "./appointment-activity";
import {
  FirebaseAppointment,
  PaymentAssosiatedToAnAppointment,
  ServiceItemForAppointment,
  VideoCallMeta,
} from "../../firestore/types";
import { getRoomLinksWhenPlatformIsEKA } from "../../apis/tryst/tele-be-v2/useCreateRoomLinksForAID";
import { track } from "../../utils/tracking";
import { notifyClientONExistingAppointment } from "./notify-existing-apt";

export default async function bookSlot({
  aid: appointmentId,
  startTime,
  endTime,
  channel,
  clinicId,
  mode,
  action,
  patient,
  createNewAppt,
  vc_meta,
  doctorId,
  tags,
  packages,
  extras,
  services = [],
  payment,
}: BookSlotParams): Promise<ControllerReturnType<FirebaseAppointment>> {
  try {
    const aid = appointmentId || generateAppointmentId();
    const myProfile = getMe();
    const [patientData] = (await getB().getPatientsByIds([patient.oid])) || [];

    let token: number | undefined = undefined;
    const isAptToBeCheckedIn =
      action === APPOINTMENT_STATUS.CHECKED_IN ||
      action === "BOOK_AND_CHECK_IN";
    const isIPDApt = action === APPOINTMENT_STATUS.COMPLETED_IP;

    if (isAptToBeCheckedIn) {
      token = await generateAppointmentToken({
        clinicId,
        doctorId,
        date: new Date(startTime),
      });
    }

    if (!patient.allowMultiBooking && !createNewAppt) {
      const liveApt = await checkExistingApt({
        date: new Date(startTime),
        doctorId,
        patientOid: patient.oid,
        clinicId,
      });

      if (liveApt.exists) {
        track("appbookedexisting", {
          aid: liveApt.appointment!.aid,
          requested_aid: aid,
        });
        notifyClientONExistingAppointment();

        return { success: true, data: liveApt.appointment! };
      }
    }
    // Handle tele flow for eka platform
    let finalVcMeta = vc_meta;
    if (
      mode === MODES_OF_APT.TELE &&
      vc_meta?.platform === SupportedTeleProviders.eka
    ) {
      const tele = await getRoomLinksWhenPlatformIsEKA(aid);
      if (tele) {
        finalVcMeta = {
          platform: SupportedTeleProviders.eka,
          host_link: tele?.host_url,
          meet_link: tele?.guest_url,
        };
      }
    }

    const appointment = await createAppointment({
      data: {
        aid,
        start_time: isoStringToTimestamp(startTime)!,
        end_time: isoStringToTimestamp(endTime)!,
        date: isoStringToTimestamp(
          dayjs(startTime).startOf("day").toISOString()
        )!,
        full_date: isoStringToTimestamp(startTime)!,
        archive: false,
        bid: myProfile.business.id,
        clinic_id: clinicId,
        tags,
        channel,
        mode,
        comm_meta: [],
        doctor_oid: doctorId,
        expires_at: isoStringToTimestamp(
          dayjs(startTime).add(1, "year").toISOString()
        )!,
        patient_oid: patient.oid,
        patient_uuid: patientData?.uuid,
        receipts: [],
        token,
        status: isIPDApt
          ? APPOINTMENT_STATUS.COMPLETED_IP
          : isAptToBeCheckedIn
            ? APPOINTMENT_STATUS.CHECKED_IN
            : APPOINTMENT_STATUS.BOOKED,
        apt_fields: [],
        assessments: {},
        visit_checkin: isAptToBeCheckedIn
          ? convertDateToFirestoreTimestamp(new Date())
          : undefined,
        visit_type: TYPES_OF_APT.REGULAR,
        vc_meta: formatVcMeta({
          vcMeta: finalVcMeta,
          patient: patientData,
          docId: doctorId,
          mode,
        }),
        inclusion_ids: packages?.inclusion_ids,
        ...(extras && { ...extras }),
        services,
        payment,
      },
      aid,
    });

    sendAppointmentCommunication({
      aid,
      data: { msg_type: MESSAGE_TYPE.SCHEDULE_APT_REMIDNER },
    });

    sendAppointmentCommunication({
      aid,
      data: { msg_type: MESSAGE_TYPE.BOOK_APPPOINTMENT },
    });

    trackAppointmentActivity({ aid, activity: AptActivityType.CREATED });
    return { success: true, data: appointment };
  } catch (e) {
    return logAndGetErrorResponse(e);
  }
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window.bookSlot = bookSlot;
async function checkExistingApt({
  date,
  doctorId,
  clinicId,
  patientOid,
}: {
  date: Date;
  doctorId: string;
  patientOid: string;
  clinicId: string;
}): Promise<{ appointment?: FirebaseAppointment; exists: boolean }> {
  const existingApts = await getB().appointments.query({
    clinicId,
    doctorIds: [doctorId],
    date,
    getArchived: false,
    status: [
      APPOINTMENT_STATUS.BOOKED,
      APPOINTMENT_STATUS.CHECKED_IN,
      APPOINTMENT_STATUS.ONGOING,
    ],
    patientOid,
  });

  const existingApt = existingApts?.docs?.[0]?.data() as FirebaseAppointment;
  return { exists: !!existingApt, appointment: existingApt };
}

function formatVcMeta({
  vcMeta,
  patient,
  docId,
  mode,
}: {
  vcMeta: BookSlotParams["vc_meta"];
  patient: LocalPatient;
  docId: string;
  mode: MODES_OF_APT;
}): VideoCallMeta | undefined {
  try {
    if (!vcMeta || mode === "in_clinic") {
      return;
    }

    const { platform, host_link, meet_link } = vcMeta;

    if (
      platform !== SupportedTeleProviders["100MS"] &&
      platform !== SupportedTeleProviders["eka"]
    ) {
      return vcMeta;
    }

    const hostUrl = new URL(host_link!);
    const meetUrl = new URL(meet_link!);

    const myProfile = getMe();

    const doc = myProfile.doctors.find((item) => item.oid === docId);

    hostUrl.searchParams.set("name", doc?.name || "Doctor");
    meetUrl.searchParams.set("name", patient.personal.name.f);

    return {
      platform: vcMeta.platform,
      host_link: hostUrl.toString(),
      meet_link: meetUrl.toString(),
    };
  } catch (e) {
    console.log("ERROR =====> formatVcMeta", e);
  }
}

export type BookSlotParams = {
  aid?: string;
  doctorId: string;
  startTime: string;
  endTime: string;
  clinicId: string;
  mode: MODES_OF_APT;
  action?:
    | APPOINTMENT_STATUS.BOOKED
    | APPOINTMENT_STATUS.CHECKED_IN
    | "BOOK_AND_CHECK_IN"
    | APPOINTMENT_STATUS.COMPLETED_IP;
  channel:
    | APPOINTMENT_CHANNEL.DOCTOR
    | APPOINTMENT_CHANNEL.STAFF
    | APPOINTMENT_CHANNEL.WALKIN;
  tags?: Array<TagRecord["tag_id"]>;
  patient: { oid: string; allowMultiBooking?: boolean };
  createNewAppt?: boolean;
  vc_meta?: VideoCallMeta;
  packages?: {
    inclusion_ids?: number[];
  };
  services?: ServiceItemForAppointment[];
  payment?: PaymentAssosiatedToAnAppointment;
  extras?: {
    [key: string]: string;
  };
};
